<template>
  <section
    v-if="proposals.length"
    class="block-proposals article-content"
    data-test="block-proposals"
  >
    <div class="block-proposals__intro">
      <h2 class="block-proposals__title">
        {{ $t('article.blockProposals.title') }}
      </h2>
      <p
        class="block-proposals__lead"
        :data-test="settled ? 'proposals-settled' : undefined"
      >
        {{ settled ? $t('article.blockProposals.settled') : $t('article.blockProposals.lead') }}
      </p>
    </div>
    <article
      v-for="item in proposals"
      :key="item.id"
      class="block-proposal"
      :data-test="'block-proposal-' + item.id"
      :data-state="item.state"
    >
      <div class="block-proposal__pair">
        <div class="block-proposal__col">
          <h3 class="block-proposal__label">
            {{ item.kind === 'insert' ? $t('article.blockProposals.newSection') : $t('article.blockProposals.original') }}
          </h3>
          <p
            v-if="item.originalHeading"
            class="block-proposal__heading"
          >
            {{ item.originalHeading }}
          </p>
          <div
            v-if="item.originalBody"
            class="block-proposal__body"
            v-html="originalBody(item)"
          />
        </div>
        <div class="block-proposal__col">
          <h3 class="block-proposal__label">
            {{ $t('article.blockProposals.proposal') }}
          </h3>
          <template v-if="item.state === 'failed'">
            <p class="block-proposal__body">
              {{ failureCopy(item) }}
            </p>
            <div class="block-proposal__actions block-proposal__actions--inline">
              <button
                type="button"
                class="block-proposal__button"
                :data-test="'retry-' + item.id"
                @click="$emit('retry', item.id)"
              >
                {{ $t('article.blockProposals.retry') }}
              </button>
              <button
                type="button"
                class="block-proposal__button"
                :data-test="'keep-' + item.id"
                @click="$emit('choose', item.id, 'keep')"
              >
                {{ $t('article.blockProposals.keep') }}
              </button>
            </div>
          </template>
          <template v-else>
            <p
              v-if="item.reason === 'table-kept'"
              class="block-proposal__warning"
              data-test="proposal-table-kept"
            >
              {{ $t('article.blockProposals.tableKept') }}
            </p>
            <p
              v-else-if="item.reason === 'figures-kept'"
              class="block-proposal__warning"
              data-test="proposal-figures-kept"
            >
              {{ $t('article.blockProposals.figuresKept') }}
            </p>
            <p
              v-if="item.numberWarning"
              class="block-proposal__warning"
              data-test="proposal-number-warning"
            >
              {{ $t('article.blockProposals.warningMissing') }}
            </p>
            <p
              v-if="item.proposedHeading"
              class="block-proposal__heading"
            >
              {{ item.proposedHeading }}
            </p>
            <div
              class="block-proposal__body"
              v-html="displayBody(item)"
            />
          </template>
        </div>
      </div>
      <div
        v-if="item.state === 'pending'"
        class="block-proposal__actions"
      >
        <button
          type="button"
          class="block-proposal__button block-proposal__button--accept"
          :data-test="'accept-' + item.id"
          @click="$emit('choose', item.id, 'accept')"
        >
          {{ $t('article.blockProposals.accept') }}
        </button>
        <button
          type="button"
          class="block-proposal__button"
          :data-test="'keep-' + item.id"
          @click="$emit('choose', item.id, 'keep')"
        >
          {{ $t('article.blockProposals.keep') }}
        </button>
      </div>
      <p
        v-else-if="item.state === 'accepted' || item.state === 'kept'"
        class="block-proposal__state"
      >
        {{ item.state === 'accepted' ? $t('article.blockProposals.accepted') : $t('article.blockProposals.kept') }}
      </p>
    </article>
  </section>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import type {BlockProposal} from '@/model/Article';
    import {stripLeadingHeading} from '@/service/analyzeFidelity';
    import {proposalsAreSettled} from '@/service/blockProposals';
    import {sanitizeFragmentHtml} from '@/utils/sanitizeHtml';

    export default Vue.extend({
        name: 'BlockProposals',
        props: {
            proposals: {
                type: Array as PropType<BlockProposal[]>,
                default: () => [],
            },
        },
        computed: {
            settled(): boolean {
                return proposalsAreSettled(this.proposals);
            },
        },
        methods: {
            originalBody(item: BlockProposal): string {
                return sanitizeFragmentHtml(item.originalBody);
            },
            displayBody(item: BlockProposal): string {
                return sanitizeFragmentHtml(stripLeadingHeading(
                    stripLeadingHeading(item.proposedBody, item.originalHeading),
                    item.proposedHeading,
                ));
            },
            failureCopy(item: BlockProposal): string {
                if (item.failure === 'missing-numbers') {
                    return String(this.$t('article.blockProposals.failedMissing'));
                }
                if (item.failure === 'invented-numbers') {
                    return String(this.$t('article.blockProposals.failedInvented'));
                }
                if (item.failure === 'changed-number-meaning') {
                    return String(this.$t('article.blockProposals.failedChangedNumberMeaning'));
                }
                if (item.failure === 'instruction-leak') {
                    return String(this.$t('article.blockProposals.failedInstructionLeak'));
                }
                if (item.reason === 'empty-answer') {
                    return String(this.$t('article.blockProposals.failedEmptyAnswer'));
                }
                return String(this.$t('article.blockProposals.failedUnusable'));
            },
        },
    });
</script>

<style scoped lang="sass">
.block-proposals
  margin: 16px auto 8px
  box-sizing: border-box

.block-proposals__intro
  margin-bottom: 16px
  padding: 10px 14px 8px

.block-proposals__title,
.block-proposals__lead
  padding-left: 16px

.block-proposals__title
  margin: 0 0 4px
  font-size: 1.25rem

.block-proposals__lead
  margin: 0 0 0
  color: rgba(0, 0, 0, 0.62)

.block-proposal
  margin: 0 0 16px
  padding: 10px 14px 8px
  border: 1px solid rgba(0, 0, 0, 0.12)
  border-radius: 12px
  background: #fff

.block-proposal__pair
  display: grid
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)
  gap: 16px
  align-items: stretch

.block-proposal__col
  min-width: 0
  padding: 10px 12px 8px 16px
  border-radius: 8px
  background: #f6f7f9

.block-proposal__label
  margin: 0 0 8px
  font-size: 0.85rem
  font-weight: 600

.block-proposal__heading
  margin: 0 0 8px
  font-weight: 700

.block-proposal__body
  margin: 0
  ::v-deep table
    display: block
    max-width: 100%
    overflow-x: auto

.block-proposal__warning
  margin: 0 0 8px
  color: #8a5a00

.block-proposal__actions
  display: flex
  flex-wrap: wrap
  gap: 8px
  margin-top: 12px

.block-proposal__actions--inline
  margin-top: 8px

.block-proposal__button
  min-height: 44px
  padding: 8px 16px
  border: 1px solid var(--sw-primary-fill)
  border-radius: 8px
  background: #fff
  color: var(--sw-primary-fill)
  font: inherit
  cursor: pointer

.block-proposal__button--accept
  background: var(--sw-primary-fill)
  color: #fff

.block-proposal__state
  margin: 12px 0 0
  font-weight: 600

@media (max-width: 900px)
  .block-proposal__pair
    grid-template-columns: minmax(0, 1fr)
</style>
